import { isAnon } from '@anm/auth/helpers/authAnon';
import hasAuthAccess from '@anm/auth/helpers/hasAuthAccess';
import { compose, hoistStatics, lifecycle, withHandlers, InferableComponentEnhancer } from 'recompose';

import userNotifications, { EMAIL_CONFIRM_REQUEST } from '../helpers/notifications/userNotifications';

import withNotification, { WithNotificationProps } from './withNotification';
import withRedirects, { WithRedirectsProps } from './withRedirects';
import withUser, { WithUserProps } from './withUser';

type SignUpRedirectProps = WithUserProps & WithRedirectsProps & Handlers & WithNotificationProps;
type Handlers = {
  showEmailConfirmNotification: () => void;
};

const componentDidUpdate = (() => {
  let prevEmail: string;

  return function componentDidUpdate(this: { props: SignUpRedirectProps }) {
    const {
      user: { profile },
      signUpRedirect,
      showEmailConfirmNotification
    } = this.props;

    if (!hasAuthAccess() || !profile || !profile.email || prevEmail || isAnon()) {
      return;
    }

    prevEmail = profile.email;
    profile.emailConfirmed ? signUpRedirect() : showEmailConfirmNotification();
  };
})();

const showEmailConfirmNotification = ({
  user: { profile },
  signUpRedirect,
  notificationActions
}: WithUserProps & WithNotificationProps & WithRedirectsProps) => () =>
  notificationActions.activate({
    onClose: signUpRedirect,
    ...userNotifications(
      EMAIL_CONFIRM_REQUEST,
      () => {
        signUpRedirect();
        notificationActions.deactivate();
      },
      {
        email: profile!.email
      }
    )
  });

const withSignUpRedirectNotification = compose(
  withUser,
  withRedirects,
  withNotification,
  withHandlers<WithUserProps & WithNotificationProps & WithRedirectsProps, Handlers>({
    showEmailConfirmNotification
  }),
  lifecycle<SignUpRedirectProps, {}>({
    componentDidUpdate
  })
) as InferableComponentEnhancer<{}>;

export default hoistStatics(withSignUpRedirectNotification);
